import React, { useEffect, useRef, useState } from 'react';
import { Animated, Image, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';

const LOGO = require('../../../assets/brand/duchef-logo.png');

/** What the intro reads back: the three taste chips, then one allergies line. */
export interface BeatReading {
  chips: string[];
  allergies: string;
}

interface Props {
  /**
   * `intro` plays right after the last question (#721 follow-up): it reads the
   * cook's answers back one at a time and ends on « Je dresse ton profil… ».
   * `visible` is the short hold while a week is rebuilt after a flip.
   */
  state: 'intro' | 'visible' | 'error' | 'empty';
  reading?: BeatReading;
  /** Called once the intro has played to its last line. */
  onIntroDone?: () => void;
  /** Fade out, then call `onClosed`. */
  closing?: boolean;
  onClosed?: () => void;
  onRetry: () => void;
  /** Only reached from the 'empty' state — retrying exclusions changes nothing. */
  onEditSafety?: () => void;
}

/**
 * The intro's timeline, in ms from the start. Long enough to read, short
 * enough not to feel like a loading screen: each answer lands on its own beat,
 * so the profile is seen being put together rather than announced.
 */
export const INTRO_STEPS = {
  firstChip: 900,
  chipGap: 550,
  allergies: 2700,
  building: 3500,
  done: 4800,
} as const;

function prefersReducedMotion(): boolean {
  return Platform.OS === 'web' && typeof window !== 'undefined'
    && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/** Fades and lifts its child in once, on mount. */
function Arrive({ children, still }: { children: React.ReactNode; still: boolean }) {
  const t = useRef(new Animated.Value(still ? 1 : 0)).current;
  useEffect(() => {
    if (still) return;
    Animated.timing(t, { toValue: 1, duration: 420, useNativeDriver: false }).start();
  }, [t, still]);
  const translateY = t.interpolate({ inputRange: [0, 1], outputRange: [8, 0] });
  return <Animated.View style={{ opacity: t, transform: [{ translateY }] }}>{children}</Animated.View>;
}

/**
 * The branded interstitial of the onboarding: the mark with a breathing halo.
 * As an intro it reads the cook's answers back before the profile screen; as a
 * hold it covers a rebuild; on failure it offers a way on.
 */
export function ProfileBeat({ state, reading, onIntroDone, closing, onClosed, onRetry, onEditSafety }: Props) {
  const { t } = useTranslation();
  const still = prefersReducedMotion();
  const scale = useRef(new Animated.Value(1)).current;
  const fade = useRef(new Animated.Value(1)).current;
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (still) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(scale, { toValue: 1.08, duration: 1700, useNativeDriver: false }),
        Animated.timing(scale, { toValue: 1, duration: 1700, useNativeDriver: false }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [scale, still]);

  // The intro's clock: one timer per step, so each answer lands on its own beat.
  const introDone = useRef(onIntroDone);
  introDone.current = onIntroDone;
  useEffect(() => {
    if (state !== 'intro') return;
    const chips = reading?.chips.length ?? 0;
    const marks = [
      ...Array.from({ length: chips }, (_, i) => INTRO_STEPS.firstChip + i * INTRO_STEPS.chipGap),
      INTRO_STEPS.allergies,
      INTRO_STEPS.building,
    ];
    const timers = marks.map((at) => setTimeout(() => setElapsed(at), at));
    timers.push(setTimeout(() => introDone.current?.(), INTRO_STEPS.done));
    return () => timers.forEach(clearTimeout);
  }, [state, reading?.chips.length]);

  const closed = useRef(onClosed);
  closed.current = onClosed;
  useEffect(() => {
    if (!closing) return;
    Animated.timing(fade, { toValue: 0, duration: still ? 0 : 380, useNativeDriver: false })
      .start(() => closed.current?.());
  }, [closing, fade, still]);

  const intro = state === 'intro';
  const chipsShown = intro && reading
    ? reading.chips.filter((_, i) => elapsed >= INTRO_STEPS.firstChip + i * INTRO_STEPS.chipGap)
    : [];
  const line = intro
    ? t(elapsed >= INTRO_STEPS.building ? 'onboarding.safety.beat' : 'onboarding.safety.beatReading')
    : t('onboarding.safety.beatWeek');

  return (
    <Animated.View style={[styles.overlay, { opacity: fade }]} testID="onb-beat" accessibilityLiveRegion="polite">
      <Animated.View style={[styles.halo, { transform: [{ scale }] }]} />
      <Image source={LOGO} style={styles.logo} resizeMode="contain" />
      {state === 'intro' || state === 'visible' ? (
        <View style={styles.reading}>
          <Arrive key={line} still={still}>
            <Text style={styles.line} testID="onb-beat-line">{line}</Text>
          </Arrive>
          {intro && (
            <View style={styles.chips} testID="onb-beat-chips">
              {chipsShown.map((chip) => (
                <Arrive key={chip} still={still}>
                  <View style={styles.chip}><Text style={styles.chipText}>{chip}</Text></View>
                </Arrive>
              ))}
            </View>
          )}
          {intro && reading && elapsed >= INTRO_STEPS.allergies && (
            <Arrive still={still}>
              <Text style={styles.allergies} testID="onb-beat-allergies">{reading.allergies}</Text>
            </Arrive>
          )}
        </View>
      ) : state === 'empty' ? (
        // Nothing survived the cook's own exclusions. A retry would return the
        // same nothing, so the only control offered is the way back.
        <View style={styles.error}>
          <Text style={styles.line}>{t('onboarding.safety.beatEmpty')}</Text>
          <Pressable
            onPress={onEditSafety}
            style={styles.retry}
            testID="onb-edit-exclusions"
            accessibilityRole="button"
          >
            <Text style={styles.retryText}>{t('onboarding.safety.editExclusions')}</Text>
          </Pressable>
        </View>
      ) : (
        <View style={styles.error}>
          <Text style={styles.line}>{t('onboarding.safety.beatError')}</Text>
          <Pressable onPress={onRetry} style={styles.retry} testID="onb-retry" accessibilityRole="button">
            <Text style={styles.retryText}>{t('onboarding.safety.retry')}</Text>
          </Pressable>
        </View>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlay: { ...StyleSheet.absoluteFillObject, backgroundColor: Paprika.background, alignItems: 'center', justifyContent: 'center', gap: 22, zIndex: 50, paddingHorizontal: 24 },
  halo: { position: 'absolute', width: 260, height: 260, borderRadius: 130, backgroundColor: Paprika.brandTint },
  logo: { width: 150, height: 150 * (116 / 330), opacity: 0.55 },
  // Fixed height: the lines and chips arrive one by one, and the mark above
  // them must not jump each time something lands.
  reading: { alignItems: 'center', gap: 14, minHeight: 150 },
  line: { fontFamily: 'Newsreader-Italic', fontSize: 22, color: Paprika.textPrimary, textAlign: 'center' },
  chips: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 8, maxWidth: 340 },
  chip: { paddingVertical: 7, paddingHorizontal: 12, borderRadius: 999, backgroundColor: Paprika.cardBg, boxShadow: '0 6px 18px -10px rgba(82, 68, 60, 0.35)' },
  chipText: { fontFamily: 'Inter-Medium', fontSize: 13, color: Paprika.textPrimary },
  allergies: { fontFamily: 'Inter-Regular', fontSize: 13, color: Paprika.textSecondary, textAlign: 'center' },
  error: { alignItems: 'center', gap: 14 },
  retry: { paddingVertical: 10, paddingHorizontal: 18, borderRadius: 12, backgroundColor: Paprika.accent },
  retryText: { fontFamily: 'Inter-SemiBold', fontSize: 13, color: '#ffffff' },
});
